import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import cx from 'classnames';
import { Button } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import { SETTINGS_FIELDS, parseSettingsForm, settingsToTexts } from '../../../helpers/inspectionSettingsForm';
import { fetchInspectionSettings, saveInspectionSettings } from '../../../actions/Inspection/inspection';
import { demoRestriction } from '../../../helpers/DemoRestriction/demoRestriction';
import cs from '../../commonStyle.css';
import s from './InspectionSettings.css';

function Switch({ id, checked, onChange, disabled }) {
  return (
    <label className={s.switch} htmlFor={id}>
      <input id={id} type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className={s.switchTrack} aria-hidden="true" />
    </label>
  );
}

// Admin page "Check-in / check-out": activation, windows, grace delay, photo limits and dispute window (R13 / D18).
// Written to the SiteSettings rows read by both GOZY-WEB and GOZY-API; the server refuses out-of-range values.
export function InspectionSettings({ intl, dispatch }) {
  const { formatMessage } = intl;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [texts, setTexts] = useState(settingsToTexts());
  const [errors, setErrors] = useState({});
  const [timeZone, setTimeZone] = useState('');
  const [feedback, setFeedback] = useState(null);

  const apply = (result) => { setTexts(settingsToTexts(result)); setTimeZone(result.timeZone || ''); setErrors({}); };

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await dispatch(fetchInspectionSettings());
      if (!active) return;
      if (result && result.status === 'success') apply(result);
      else setFeedback({ type: 'error', text: (result && result.errorMessage) || formatMessage(messages.inspAdmLoadFailed) });
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const onSave = async (event) => {
    event.preventDefault();
    if (saving) return;
    const parsed = parseSettingsForm(texts);
    setErrors(parsed.errors);
    if (Object.keys(parsed.errors).length) return;
    const restricted = await dispatch(demoRestriction({}));
    if (restricted) return;
    setSaving(true);
    setFeedback(null);
    const result = await dispatch(saveInspectionSettings(parsed.value));
    setSaving(false);
    if (result && result.status === 'success') {
      apply(result);
      setFeedback({ type: 'success', text: formatMessage(messages.inspAdmSaved) });
    } else {
      setFeedback({ type: 'error', text: (result && result.errorMessage) || formatMessage(messages.inspAdmSaveFailed) });
    }
  };

  return (
    <div aria-busy={loading}>
      <h1 className="siteAdminHeading">{formatMessage(messages.inspAdmTitle)}</h1>
      {feedback && (
        <div className={cx(s.banner, feedback.type === 'error' ? s.bannerError : s.bannerSuccess)} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.text}</div>
      )}
      {!loading && (
        <form onSubmit={onSave} noValidate>
          <section className={s.panel}>
            <h2 className={s.panelTitle}>{formatMessage(messages.inspAdmActivationTitle)}</h2>
            <div className={s.panelBody}>
              <div className={s.switchRow}>
                <span className={s.switchName} id="insp-adm-enabled-label">{formatMessage(messages.inspAdmEnabled)}</span>
                <Switch id="insp-adm-enabled" checked={texts.inspectionEnabled} disabled={saving} onChange={(checked) => setTexts((current) => ({ ...current, inspectionEnabled: checked }))} />
              </div>
              <p className={s.help}>{formatMessage(messages.inspAdmEnabledHelp)}</p>
              {timeZone && <p className={s.help}>{formatMessage(messages.inspAdmTimeZone, { zone: timeZone })}</p>}
            </div>
          </section>

          <section className={s.panel}>
            <h2 className={s.panelTitle}>{formatMessage(messages.inspAdmWindowsTitle)}</h2>
            <div className={s.panelBody}>
              <div className={s.grid}>
                {SETTINGS_FIELDS.map((field) => {
                  const id = `insp-adm-${field.name}`;
                  return (
                    <div key={field.name} className={s.field}>
                      <label className={s.label} htmlFor={id}>{formatMessage(messages[field.labelKey])}</label>
                      <input
                        id={id}
                        type="number"
                        inputMode="numeric"
                        min={field.min}
                        max={field.max}
                        step={1}
                        className="form-control commonInputField"
                        value={texts[field.name]}
                        aria-invalid={errors[field.name] ? 'true' : undefined}
                        aria-describedby={`${id}-help`}
                        onChange={(event) => { const value = event.target.value; setTexts((current) => ({ ...current, [field.name]: value })); }}
                      />
                      <p id={`${id}-help`} className={s.help}>{formatMessage(messages[field.helpKey])}</p>
                      {errors[field.name] && <p className={s.errorText} role="alert">{formatMessage(messages[errors[field.name]], { min: field.min, max: field.max })}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <div className={s.actions}>
            <Button className={cx(cs.btnPrimary)} type="submit" disabled={saving}>{formatMessage(messages.save)}</Button>
          </div>
        </form>
      )}
    </div>
  );
}

InspectionSettings.propTypes = { title: PropTypes.string };

export default injectIntl(withStyles(s)(connect()(InspectionSettings)));
